// This file contains helper functions for payment info fields.

import { isValidExpirationDate, makeValidator } from '@eventbrite/eds-utils';
import { gettext } from '@eventbrite/i18n';
import last from 'lodash/last';
import { stripAllBlanks, stripNonNumbers, stripRepeatedZeros } from '../utils';
import { CARD_EXPIRATION_DATE_FIELD_NAME } from './constants';

const NUMBER_BETWEEN_2_AND_9 = /^[2-9]$/;
const SEPARATOR = '  /  ';

const isMonthGreaterThan12 = (date: string) =>
    parseInt(date.substring(0, 2)) > 12;

const isNumberBetween2And9 = (text: string) =>
    NUMBER_BETWEEN_2_AND_9.test(text);

const isZero = (text: string) => text === '0';

const isDeleting = (date: string, oldDate: string) =>
    oldDate ? date.length - oldDate.length === -1 : false;

const hasEnteredANonNumberAfterAOne = (
    date: string,
    oldDate: string,
    normalizedDate: string,
) =>
    normalizedDate === '1' &&
    date.length === 2 &&
    (!oldDate || oldDate.length === 1);

/**
 * @param  {string} date     The new date to be parsed.
 * @param  {string} oldDate  The old date to compare and know if a `/` should be added.
 * @return {string}
 */
export const normalizeExpirationDate = (date: string, oldDate: string) => {
    let cardExpirationDate = date;

    // When an input of 2 or more chars is present. This allows to paste text typing, and deleting
    if (cardExpirationDate.length >= 2) {
        let isGoingBackwards = last(oldDate) === '/';
        let [month = '', year = ''] = date.split('/');

        // In case a value like `1234` or `123/` is received, the month will have an invalid value.
        if (month.length > 2) {
            isGoingBackwards = false;
            /**
             * Since the month should only have 2 digits the rest is taken as the year.
             * In case a value like `121/5` is received it should not delete the last number of the year
             * we had.
             */
            year = month.substr(2, 2) + year;
            month = month.substr(0, 2);
        }

        // If there was no date at start or we are not deleting the input to change something
        if (!oldDate || !isGoingBackwards) {
            cardExpirationDate = `${month}/${year}`;
        }
    }

    return cardExpirationDate;
};

const normalizeDateGreaterThan2 = (
    date: string,
    oldDate: string,
    normalizedDate: string,
) => {
    const dateWithoutBlanks = stripAllBlanks(date);
    if (isDeleting(date, oldDate)) {
        const oldDateWithoutBlanks = stripAllBlanks(oldDate);
        const [newMonth, newYear] = dateWithoutBlanks.split('/');
        const [oldMonth] = oldDateWithoutBlanks.split('/');

        // If the user is deleting in the month:
        // - If the remaininig number is zero, let only the zero
        // - If the remaining number is not a zero, put a zero before it and keep the year
        if (isDeleting(newMonth, oldMonth)) {
            return isZero(newMonth)
                ? newMonth
                : `0${newMonth}${SEPARATOR}${newYear}`;
        }
    }

    const month = normalizedDate.substring(0, 2);
    const year = normalizedDate.substring(2, 4);
    return `${month}${SEPARATOR}${year}`;
};

const normalizeDateEqual2 = (
    date: string,
    oldDate: string,
    normalizedDate: string,
) =>
    // If the last character in the field is the separator and the user is deleting
    // only the first character will remain in the field
    isDeleting(date, oldDate) && oldDate.endsWith(SEPARATOR)
        ? normalizedDate.substring(0, 2)
        : `${normalizedDate}${SEPARATOR}`;

/**
 * @param  {string} date     The new date to be parsed.
 * @param  {string} oldDate  The old date to compare and know if a `/` should be added.
 * @return {string}
 */
export const marmaladeNormalizeExpirationDate = (
    date: string,
    oldDate: string,
) => {
    const dateWithoutBlanks = stripAllBlanks(date);

    // Removing non numbers and adding a 0 at the beginning when:
    // 1. the two first numbers are greater than 12
    // 2. the first number is between 2 and 9
    let normalizedDate = stripRepeatedZeros(stripNonNumbers(dateWithoutBlanks));

    // If the date doesn't start with a zero and its two first characters are greater than 12
    // we have to set a zero before it to display the proper month
    normalizedDate =
        (!isZero(normalizedDate[0]) &&
        (isMonthGreaterThan12(normalizedDate) ||
            isNumberBetween2And9(normalizedDate[0]))
            ? '0'
            : '') + normalizedDate;

    return normalizedDate.length > 2
        ? normalizeDateGreaterThan2(date, oldDate, normalizedDate)
        : normalizedDate.length === 2
        ? normalizeDateEqual2(date, oldDate, normalizedDate)
        : hasEnteredANonNumberAfterAOne(date, oldDate, normalizedDate)
        ? // If there is a 1 in the field and the user enters a non number (special char or letter)
          // it will be formatted to "01 / " automatically
          `0${normalizedDate}${SEPARATOR}`
        : normalizedDate;
};

// This validator object should be imported by each Redux Form that needs to
// use the CardExpirationDateField component. You can see an example of how to use
// it here:
// https://github.com/eventbrite/eventbrite_design_system/tree/master/website/docs/components/cardExpirationDateField/exampleForm.js
export const CARD_EXPIRATION_DATE_FIELD_VALIDATOR = makeValidator({
    [CARD_EXPIRATION_DATE_FIELD_NAME]: {
        validator: isValidExpirationDate,
        errorMessage: gettext('Please enter a valid expiration date.'),
    },
});
